/* eslint-disable no-restricted-globals, no-restricted-properties -- see the module doc. */

/**
 * The console's single wall-clock read point.
 *
 * `docs/plan/06-risks.md` R5 bans `Date.now()` / `new Date()` outside a `Clock` implementation,
 * and it bans them for a good reason here specifically: this UI renders seven different
 * deadlines (reservation TTL, evidence deadline, review SLA, late-slip grace, withdrawal
 * max-age, verification max, credit retry). If any component reads the clock directly, that
 * countdown becomes untestable without `sleep()`.
 *
 * Everything else in `apps/console` imports {@link nowMs} / {@link now} from here, or — better —
 * takes a {@link Clock} so tests can drive time explicitly.
 */

/** Source of the current time. Mirrors `@pf/shared`'s `Clock` port. */
export interface Clock {
  /** The current instant. */
  now(): Date;
  /** The current instant in epoch milliseconds. */
  nowMs(): number;
}

/** The browser clock. */
export const systemClock: Clock = {
  now: () => new Date(),
  nowMs: () => Date.now(),
};

/** Current instant, from the system clock. */
export function now(): Date {
  return systemClock.now();
}

/** Current instant in epoch milliseconds, from the system clock. */
export function nowMs(): number {
  return systemClock.nowMs();
}

/**
 * A clock under test control.
 *
 * ```ts
 * const clock = fixedClock('2026-07-23T10:00:00Z');
 * clock.advanceBy(minutes(10)); // the reservation TTL is now due
 * ```
 */
export interface TestClock extends Clock {
  /** Move time forward by a duration in milliseconds. */
  advanceBy(ms: number): void;
  /** Jump to a specific instant. */
  setTo(instant: Date | string): void;
}

/** Build a {@link TestClock} pinned to `start`. */
export function fixedClock(start: Date | string): TestClock {
  let current = new Date(start).getTime();
  return {
    now: () => new Date(current),
    nowMs: () => current,
    advanceBy(ms) {
      if (ms < 0) throw new RangeError('clock cannot move backwards');
      current += ms;
    },
    setTo(instant) {
      current = new Date(instant).getTime();
    },
  };
}

/** Parse an ISO-8601 instant to epoch milliseconds. */
export function toEpochMs(iso: string): number {
  return new Date(iso).getTime();
}

/**
 * Parse an ISO-8601 instant into a `Date`.
 *
 * Parsing a *given* string is not a wall-clock read, but the R5 lint rule is AST-only and cannot
 * tell the difference — so the conversion lives here, in the one module allowed to name `Date`,
 * rather than being disabled ad hoc at a dozen call sites.
 */
export function toDate(iso: string): Date {
  return new Date(iso);
}

/** Convert an epoch-milliseconds instant to an ISO-8601 string with offset. */
export function toIso(epochMs: number): string {
  return new Date(epochMs).toISOString();
}

/**
 * Parse an `<input type="datetime-local">` value into epoch milliseconds.
 *
 * The control's value is **local wall clock with no offset**: `2026-08-05T08:00` means 08:00 where
 * the operator is sitting and carries nothing that says where that is. Turning it into an instant
 * is therefore a *decision* — "in this browser's zone" — and it is made here, once, rather than at
 * every call site.
 *
 * The multi-argument `Date` constructor is used rather than `new Date(value)` because it is the
 * only parse in the language defined to read its components in the local zone unconditionally.
 * The single-argument form is local for a date-*time* string but UTC for a date-*only* one, and a
 * helper whose zone depends on how much the operator managed to type is a bug waiting for a DST
 * weekend. It also folds a nonexistent local time (the hour a spring-forward skips) to the next
 * real instant instead of producing `NaN`, which is the friendlier answer for a KPI range.
 *
 * @param value - a `YYYY-MM-DDTHH:MM` value, optionally with seconds.
 * @returns epoch milliseconds, or `NaN` when the value is blank or incomplete.
 */
export function localInputToEpochMs(value: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) return Number.NaN;
  const [, year, month, day, hour, minute, second] = match;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? '0'),
    0,
  ).getTime();
}

/**
 * Render an instant as an `<input type="datetime-local">` value in the browser's local zone.
 *
 * The inverse of {@link localInputToEpochMs}, and deliberately hand-assembled: every `Intl` and
 * `toLocaleString` output is locale-shaped, while this control only ever accepts the fixed
 * `YYYY-MM-DDTHH:MM` grammar.
 */
export function epochMsToLocalInput(epochMs: number): string {
  const at = new Date(epochMs);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return [
    `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
    `${pad(at.getHours())}:${pad(at.getMinutes())}`,
  ].join('T');
}

/** Convert seconds to milliseconds. */
export const seconds = (n: number): number => n * 1_000;
/** Convert minutes to milliseconds. */
export const minutes = (n: number): number => n * 60_000;
/** Convert hours to milliseconds. */
export const hours = (n: number): number => n * 3_600_000;
/** Convert days to milliseconds. */
export const days = (n: number): number => n * 86_400_000;
